(() => {
  'use strict';

  if (customElements.get('sc-living-scene')) return;

  const VERTEX_SHADER = `
    attribute vec2 a_position;
    varying vec2 v_uv;
    void main() {
      gl_Position = vec4(a_position, 0.0, 1.0);
      // Image coordinates deliberately run from the top down.
      v_uv = vec2((a_position.x + 1.0) * 0.5, (1.0 - a_position.y) * 0.5);
    }
  `;

  const FRAGMENT_SHADER = `
    precision highp float;
    varying vec2 v_uv;
    uniform sampler2D u_original;
    uniform sampler2D u_background;
    uniform vec2 u_crop;
    uniform float u_time;

    float hash(vec2 p) {
      p = fract(p * vec2(123.34, 456.21));
      p += dot(p, p + 45.32);
      return fract(p.x * p.y);
    }

    float noise(vec2 p) {
      vec2 i = floor(p);
      vec2 f = fract(p);
      f = f * f * (3.0 - 2.0 * f);
      return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), f.x),
                 mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), f.x), f.y);
    }

    float cloud(vec2 p) {
      return noise(p) * 0.57 + noise(p * 2.07 + 13.6) * 0.28
           + noise(p * 4.21 - 9.2) * 0.15;
    }

    void edge(vec2 p, vec2 a, vec2 b, inout float distance, inout float inside) {
      vec2 v = b - a;
      vec2 w = p - a;
      distance = min(distance, length(w - v * clamp(dot(w, v) / dot(v, v), 0.0, 1.0)));
      if ((a.y > p.y) != (b.y > p.y)) {
        if (p.x < (b.x - a.x) * (p.y - a.y) / (b.y - a.y) + a.x) inside = 1.0 - inside;
      }
    }

    // The ship comes from the original art; the six-point mask follows its actual hull.
    float shipDistance(vec2 p) {
      float distance = 2.0;
      float inside = 0.0;
      vec2 a = vec2(630.0, 277.0) / 1254.0;
      vec2 b = vec2(858.0, 570.0) / 1254.0;
      vec2 c = vec2(908.0, 592.0) / 1254.0;
      vec2 d = vec2(633.0, 646.0) / 1254.0;
      vec2 e = vec2(356.0, 594.0) / 1254.0;
      vec2 f = vec2(402.0, 570.0) / 1254.0;
      edge(p, a, b, distance, inside);
      edge(p, b, c, distance, inside);
      edge(p, c, d, distance, inside);
      edge(p, d, e, distance, inside);
      edge(p, e, f, distance, inside);
      edge(p, f, a, distance, inside);
      return distance * (1.0 - 2.0 * inside);
    }

    void main() {
      // Exactly the same centre crop as object-fit: cover. The camera never moves.
      vec2 uv = (v_uv - 0.5) * u_crop + 0.5;
      float t = u_time;

      // Perspective-shaped water mask leaves both shorelines and foreground rocks anchored.
      float depth = clamp((uv.y - 0.697) / 0.275, 0.0, 1.0);
      float leftBank = 0.18 - 0.07 * smoothstep(0.710, 0.790, uv.y)
                     + 0.315 * smoothstep(0.790, 0.948, uv.y);
      float rightBank = 0.885 + 0.085 * smoothstep(0.710, 0.815, uv.y)
                      - 0.37 * smoothstep(0.850, 0.925, uv.y);
      float water = smoothstep(0.697, 0.716, uv.y)
                  * (1.0 - smoothstep(0.935, 0.958, uv.y))
                  * smoothstep(leftBank, leftBank + 0.027, uv.x)
                  * (1.0 - smoothstep(rightBank - 0.027, rightBank, uv.x));

      float wave = pow(depth + 0.023, 0.54) * 83.0 - t * 2.0;
      float wideRipple = sin(wave + sin(uv.x * 18.0 + t * 0.33) * 0.85);
      float fineRipple = sin(uv.y * 615.0 - t * 3.1 + sin(uv.x * 27.0 - t * 0.21));
      vec2 flow = vec2(
        (sin(wave * 1.31 + uv.x * 15.0) * 0.0024 + fineRipple * 0.0007) * (0.3 + depth),
        (wideRipple * 0.0028 + fineRipple * 0.00038) * (0.24 + depth)
      ) * water;
      vec3 colour = texture2D(u_original, clamp(uv + flow, 0.0, 1.0)).rgb;

      // Only inpaint the place the ship occupied. Generated plate changes elsewhere never replace the art.
      float vacatedHull = 1.0 - smoothstep(0.0015, 0.027, shipDistance(uv));
      colour = mix(colour, texture2D(u_background, uv).rgb, vacatedHull);

      // Shafts move through the existing sunlight, without moving clouds or the horizon.
      float sunDistance = max(uv.y - 0.135, 0.03);
      float rayAngle = (uv.x - 0.503) / sunDistance;
      float shaft = pow(max(0.0, sin(rayAngle * 29.0 + sin(t * 0.17) * 1.25)), 12.0);
      float cone = (1.0 - smoothstep(0.23, 0.80, abs(rayAngle)))
                 * smoothstep(0.16, 0.24, uv.y) * (1.0 - smoothstep(0.64, 0.70, uv.y));
      colour += vec3(1.0, 0.64, 0.27) * shaft * cone * 0.042;

      // Independent hull translation: about twelve source pixels each way in an eight-second cycle.
      vec2 centre = vec2(631.0, 485.0) / 1254.0;
      vec2 hover = vec2(sin(t * 0.393) * 0.00115, sin(t * 0.785398) * 0.00957);
      float angle = sin(t * 0.392699) * 0.00349;
      float cs = cos(angle);
      float sn = sin(angle);
      vec2 local = uv - centre - hover;
      vec2 shipUV = vec2(cs * local.x + sn * local.y, -sn * local.x + cs * local.y) + centre;
      float hull = 1.0 - smoothstep(-0.00065, 0.00065, shipDistance(shipUV));
      colour = mix(colour, texture2D(u_original, clamp(shipUV, 0.0, 1.0)).rgb, hull);

      // Low, rolling mist. Two differently advected noise fields avoid a sliding flat fog band.
      float mistRoll = cloud(vec2(uv.x * 10.0 - t * 0.075, uv.y * 62.0 + t * 0.033));
      float mistFold = cloud(vec2(uv.x * 17.0 + t * 0.044, uv.y * 94.0 - t * 0.027));
      float mistHeight = uv.y - 0.689 + sin(uv.x * 15.0 + t * 0.23) * 0.004;
      float mistEnvelope = exp(-pow(mistHeight / 0.019, 2.0));
      float highMist = exp(-pow((uv.y - 0.671 + sin(uv.x * 9.0 - t * 0.18) * 0.008) / 0.020, 2.0));
      float mist = (smoothstep(0.31, 0.73, mistRoll) * mistEnvelope * 0.135
                  + smoothstep(0.39, 0.77, mistFold) * highMist * 0.065)
                 * smoothstep(0.0, 0.08, uv.x) * (1.0 - smoothstep(0.92, 1.0, uv.x));
      colour = mix(colour, vec3(0.83, 0.74, 0.58), mist);

      // The reflection breaks into moving wave crests, strongest on the existing golden light path.
      float lightPath = exp(-pow((uv.x - 0.504 - flow.x * 1.8) / (0.017 + depth * 0.047), 2.0));
      float glint = pow(max(0.0, sin(wave * 1.7 + fineRipple * 0.36)), 10.0);
      colour += vec3(1.0, 0.66, 0.28) * lightPath * glint * water * 0.057;
      colour *= 1.0 + lightPath * wideRipple * water * 0.045;

      gl_FragColor = vec4(clamp(colour, 0.0, 1.0), 1.0);
    }
  `;

  class SoulCompassLivingScene extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this._generation = (this._generation || 0) + 1;
      this._abort = new AbortController();
      this._canvas = this.querySelector('canvas');
      this._fallback = this.querySelector('[data-sc-fallback]');
      this._reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      this._visible = false;
      this._ready = false;
      this._failed = false;
      this._time = 0;
      this._lastReport = -1;
      this._raf = 0;
      this._textures = [];
      this._shaders = [];
      this._hideCanvas('static');
      if (!this._canvas || !this._fallback || !this.dataset.original || !this.dataset.background) return;

      const options = { signal: this._abort.signal };
      this._reduced.addEventListener('change', () => this._refresh(), options);
      document.addEventListener('visibilitychange', () => this._refresh(), options);
      this._canvas.addEventListener('webglcontextlost', (event) => {
        event.preventDefault();
        this._fail('context-lost');
      }, options);
      this._resizeObserver = new ResizeObserver(() => this._resize());
      this._resizeObserver.observe(this);
      this._intersectionObserver = new IntersectionObserver(([entry]) => {
        this._visible = entry.isIntersecting;
        this._refresh();
      }, { threshold: 0 });
      this._intersectionObserver.observe(this);
      this._refresh();
    }

    disconnectedCallback() {
      this._connected = false;
      this._generation += 1;
      this._stop();
      this._abort?.abort();
      this._resizeObserver?.disconnect();
      this._intersectionObserver?.disconnect();
      this._dispose();
      this._loading = false;
      this._hideCanvas('static');
    }

    _hideCanvas(state) {
      if (this._canvas) this._canvas.hidden = true;
      this.removeAttribute('data-sc-ready');
      this.dataset.scState = state;
    }

    _refresh() {
      if (!this._connected || this._failed) return;
      if (this._reduced.matches) {
        this._stop();
        this._hideCanvas('reduced-motion');
        return;
      }
      if (!this._visible || document.hidden) {
        this._stop();
        if (this._ready) this.dataset.scState = 'suspended';
        return;
      }
      if (!this._ready) {
        if (!this._loading) this._initialize();
        return;
      }
      this._resize();
      if (!this._draw()) return;
      this._canvas.hidden = false;
      this.setAttribute('data-sc-ready', '');
      this.dataset.scState = 'running';
      if (!this._raf) {
        this._lastTick = 0;
        this._lastFrame = 0;
        this._raf = requestAnimationFrame((now) => this._tick(now));
      }
    }

    async _initialize() {
      this._loading = true;
      this.dataset.scState = 'loading';
      const generation = this._generation;
      try {
        const [original, background] = await Promise.all([
          this._loadImage(this.dataset.original), this._loadImage(this.dataset.background)
        ]);
        if (!this._connected || generation !== this._generation) return;
        const gl = this._canvas.getContext('webgl', {
          alpha: false, antialias: false, depth: false, stencil: false,
          premultipliedAlpha: false, preserveDrawingBuffer: false, powerPreference: 'low-power'
        });
        if (!gl) throw new Error('WebGL unavailable');
        this._gl = gl;
        this._sourceAspect = original.naturalWidth / original.naturalHeight;
        const highPrecision = gl.getShaderPrecisionFormat(gl.FRAGMENT_SHADER, gl.HIGH_FLOAT);
        const fragmentSource = highPrecision?.precision ? FRAGMENT_SHADER
          : FRAGMENT_SHADER.replace('precision highp float;', 'precision mediump float;');
        const vertex = this._compile(gl.VERTEX_SHADER, VERTEX_SHADER);
        const fragment = this._compile(gl.FRAGMENT_SHADER, fragmentSource);
        const program = gl.createProgram();
        this._program = program;
        gl.attachShader(program, vertex);
        gl.attachShader(program, fragment);
        gl.linkProgram(program);
        gl.deleteShader(vertex);
        gl.deleteShader(fragment);
        this._shaders = [];
        if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
          throw new Error(`Scene linking failed: ${(gl.getProgramInfoLog(program) || '').slice(0, 500)}`);
        }
        gl.useProgram(program);

        this._buffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, this._buffer);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
        const position = gl.getAttribLocation(program, 'a_position');
        gl.enableVertexAttribArray(position);
        gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
        for (const [unit, image] of [original, background].entries()) {
          const texture = gl.createTexture();
          this._textures.push(texture);
          gl.activeTexture(gl.TEXTURE0 + unit);
          gl.bindTexture(gl.TEXTURE_2D, texture);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
          gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
        }
        gl.uniform1i(gl.getUniformLocation(program, 'u_original'), 0);
        gl.uniform1i(gl.getUniformLocation(program, 'u_background'), 1);
        this._cropUniform = gl.getUniformLocation(program, 'u_crop');
        this._timeUniform = gl.getUniformLocation(program, 'u_time');
        if (gl.getError() !== gl.NO_ERROR) throw new Error('Scene texture upload failed');
        this._ready = true;
        this._loading = false;
        this._refresh();
      } catch (error) {
        if (this._connected && generation === this._generation) {
          console.warn('[Soul Compass] Living scene is using its static artwork.', error.message);
          this._fail('unavailable');
        }
      }
    }

    _loadImage(url) {
      return new Promise((resolve, reject) => {
        const image = new Image();
        image.crossOrigin = 'anonymous';
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error('Scene image unavailable'));
        image.src = url;
      });
    }

    _compile(type, source) {
      const shader = this._gl.createShader(type);
      this._shaders.push(shader);
      this._gl.shaderSource(shader, source);
      this._gl.compileShader(shader);
      if (!this._gl.getShaderParameter(shader, this._gl.COMPILE_STATUS)) {
        throw new Error(`Scene shader unavailable: ${(this._gl.getShaderInfoLog(shader) || '').slice(0, 500)}`);
      }
      return shader;
    }

    _resize() {
      if (!this._gl || !this._ready || !this._connected) return;
      const bounds = this.getBoundingClientRect();
      if (bounds.width <= 0 || bounds.height <= 0) return;
      const ratio = Math.min(window.devicePixelRatio || 1, 1.5, 1600 / Math.max(bounds.width, bounds.height));
      const width = Math.max(1, Math.round(bounds.width * ratio));
      const height = Math.max(1, Math.round(bounds.height * ratio));
      if (this._canvas.width !== width || this._canvas.height !== height) {
        this._canvas.width = width;
        this._canvas.height = height;
      }
      this._gl.viewport(0, 0, width, height);
      const frameAspect = bounds.width / bounds.height;
      const cropX = Math.min(1, frameAspect / this._sourceAspect);
      const cropY = Math.min(1, this._sourceAspect / frameAspect);
      this._gl.uniform2f(this._cropUniform, cropX, cropY);
      if (!this._reduced.matches && this._visible && !document.hidden) this._draw();
    }

    _draw() {
      if (!this._ready || !this._gl || this._failed) return false;
      if (this._gl.isContextLost()) {
        this._fail('context-lost');
        return false;
      }
      this._gl.uniform1f(this._timeUniform, this._time);
      this._gl.drawArrays(this._gl.TRIANGLE_STRIP, 0, 4);
      return true;
    }

    _tick(now) {
      this._raf = 0;
      if (!this._connected || !this._ready || this._failed || this._reduced.matches || !this._visible || document.hidden) return;
      if (!this._lastTick) this._lastTick = now;
      const elapsed = now - this._lastFrame;
      if (elapsed >= 1000 / 30) {
        this._time += Math.min((now - this._lastTick) / 1000, 0.12);
        this._lastTick = now;
        this._lastFrame = now - (elapsed % (1000 / 30));
        if (!this._draw()) return;
        const seconds = Math.floor(this._time);
        if (seconds !== this._lastReport) {
          this.dataset.scRenderTime = this._time.toFixed(2);
          this._lastReport = seconds;
        }
      }
      this._raf = requestAnimationFrame((next) => this._tick(next));
    }

    _stop() {
      if (this._raf) cancelAnimationFrame(this._raf);
      this._raf = 0;
      this._lastTick = 0;
      this._lastFrame = 0;
    }

    _fail(reason) {
      this._failed = true;
      this._loading = false;
      this._stop();
      this._hideCanvas(reason);
      this._dispose();
    }

    _dispose() {
      const gl = this._gl;
      if (gl && !gl.isContextLost()) {
        this._textures?.forEach((texture) => gl.deleteTexture(texture));
        this._shaders?.forEach((shader) => gl.deleteShader(shader));
        if (this._buffer) gl.deleteBuffer(this._buffer);
        if (this._program) gl.deleteProgram(this._program);
      }
      this._textures = [];
      this._shaders = [];
      this._buffer = null;
      this._program = null;
      this._gl = null;
      this._ready = false;
    }
  }

  customElements.define('sc-living-scene', SoulCompassLivingScene);
})();
